import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useSearchParams } from "react-router-dom";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";
import { toast } from "react-toastify";
import {
    FaFileAlt, FaPlus, FaEdit, FaTrash, FaTimes, FaExclamationTriangle,
} from "react-icons/fa";
import {
    getFPOTemplates, addFPOTemplate, updateFPOTemplate, deleteFPOTemplate,
} from "../../services/fpoTemplate";
import { getUserRole } from "../../helpers/local_storage_helpers";
import { resolveMediaUrl } from "../../helpers/media";
import PageHeader from "../../components/header/PageHeader";
import SearchableSelectField from "../../components/inputFields/SearchableSelectField";
import { CHANNEL_OPTIONS, ChannelBadge, NotificationTabs, Pagination } from "./shared";

const PAGE_SIZE = 10;

const TemplateSchema = Yup.object().shape({
    name:    Yup.string().required("Template name is required"),
    channel: Yup.string().oneOf(CHANNEL_OPTIONS.map((c) => c.value)).required("Channel is required"),
    title:   Yup.string(),
    body:    Yup.string(),
});

// A template is only editable by this FPO if the backend tells us it owns it —
// falls back to "attempt it, handle the 404 gracefully" when that signal is missing.
const isOwnTemplate = (t) => {
    if ("fpo" in t) return !!t.fpo;
    if ("is_owned" in t) return !!t.is_owned;
    if ("owned_by_fpo" in t) return !!t.owned_by_fpo;
    return null; // unknown — don't hide the action, just handle failure gracefully
};

const fmtDate = (iso) => {
    if (!iso) return "—";
    const d = new Date(iso);
    return isNaN(d.getTime()) ? iso : d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Modal shell ──────────────────────────────────────────────────
const Modal = ({ title, icon: Icon, onClose, children }) => (
    <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[1100] bg-black/50 backdrop-blur-sm flex items-center justify-center p-4"
        onClick={onClose}
    >
        <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.97 }}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
        >
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 sticky top-0 bg-white rounded-t-2xl z-10">
                <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Icon className="text-primary text-sm" />
                    </div>
                    <h3 className="font-bold text-gray-800 text-sm">{title}</h3>
                </div>
                <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded-lg transition-colors">
                    <FaTimes />
                </button>
            </div>
            <div className="p-5">{children}</div>
        </motion.div>
    </motion.div>
);

const FieldWrap = ({ label, name, required, children }) => (
    <div>
        <label className="block text-xs font-semibold text-gray-500 mb-1">
            {label}{required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
        {children}
        <ErrorMessage name={name} component="p" className="text-red-500 text-xs mt-1" />
    </div>
);

const inputCls = "w-full px-3 py-2.5 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary";

const FPOTemplates = () => {
    const [searchParams] = useSearchParams();
    const channelFilter = searchParams.get("channel") || "app_push";
    const userRole = getUserRole();
    const isAdmin  = userRole === "fpo_admin" || !userRole;

    const [templates, setTemplates] = useState([]);
    const [loading, setLoading]     = useState(true);
    const [error, setError]         = useState(null);

    const [search, setSearch]                 = useState("");
    const [debouncedSearch, setDebouncedSearch] = useState("");

    const [page, setPage]   = useState(1);
    const [count, setCount] = useState(0);
    const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));

    const [showModal, setShowModal]     = useState(false);
    const [editingItem, setEditingItem] = useState(null);
    const [imageFile, setImageFile]     = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [deletingItem, setDeletingItem] = useState(null);
    const [deleting, setDeleting]         = useState(false);

    const loadTemplates = async () => {
        setLoading(true);
        setError(null);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (debouncedSearch.trim()) params.search = debouncedSearch.trim();
            params.channel = channelFilter;

            const res  = await getFPOTemplates(params);
            const data = res?.data;
            if (data?.status === 200 || data?.status === "200") {
                setTemplates(data.results || []);
                setCount(data.count ?? 0);
            } else {
                setError(data?.message || "Failed to load templates.");
            }
        } catch {
            setError("Unable to connect. Please check the server.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 400);
        return () => clearTimeout(t);
    }, [search]);

    useEffect(() => {
        setPage(1);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [channelFilter]);

    useEffect(() => {
        loadTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, debouncedSearch, channelFilter]);

    const resetImageState = () => { setImageFile(null); setImagePreview(null); };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) { setImageFile(file); setImagePreview(URL.createObjectURL(file)); }
    };

    const handleSubmit = async (values, { resetForm }) => {
        try {
            let payload;
            if (imageFile) {
                payload = new FormData();
                Object.entries(values).forEach(([k, v]) => payload.append(k, v ?? ""));
                payload.append("image", imageFile);
            } else {
                payload = values;
            }

            const res = editingItem
                ? await updateFPOTemplate(editingItem.id, payload)
                : await addFPOTemplate(payload);
            const data = res?.data;
            const ok   = data?.status === 200 || data?.status === 201
                || data?.status === "200" || data?.status === "201"
                || res?.status === 200 || res?.status === 201;

            if (ok) {
                toast.success(editingItem ? "Template updated! ✅" : "Template created! ✅");
                resetForm();
                resetImageState();
                setEditingItem(null);
                setShowModal(false);
                await loadTemplates();
            } else {
                const errs = data?.errors;
                toast.error((errs && Object.values(errs).flat().join(", ")) || data?.message || "Save failed.");
            }
        } catch (e) {
            if (e?.response?.status === 404 && editingItem) {
                toast.error("This is a shared template — you can only edit templates your FPO created.");
                return;
            }
            const errs = e?.response?.data?.errors;
            toast.error((errs && Object.values(errs).flat().join(", ")) || e?.response?.data?.message || "Something went wrong.");
        }
    };

    const handleDelete = async () => {
        if (!deletingItem) return;
        setDeleting(true);
        try {
            const res  = await deleteFPOTemplate(deletingItem.id);
            const ok   = res?.status === 200 || res?.status === 204 || res?.data?.status === 200 || res?.data?.status === "200";
            if (ok) {
                toast.success("Template removed.");
                setDeletingItem(null);
                await loadTemplates();
            } else {
                toast.error(res?.data?.message || "Failed to delete.");
            }
        } catch (e) {
            if (e?.response?.status === 404) {
                toast.error("This is a shared template — you can only delete templates your FPO created.");
            } else {
                toast.error(e?.response?.data?.message || "Something went wrong.");
            }
        } finally {
            setDeleting(false);
        }
    };

    const getInitialValues = () => editingItem
        ? {
            name: editingItem.name || "",
            channel: editingItem.channel || "app_push",
            title: editingItem.title || "",
            body: editingItem.body || "",
        }
        : { name: "", channel: "app_push", title: "", body: "" };

    return (
        <div className="w-full pb-12">
            <PageHeader
                title="Message Templates"
                search={search}
                onSearchChange={setSearch}
                searchPlaceholder="Search by name or code…"
                actionLabel={isAdmin ? "New Template" : undefined}
                actionIcon={FaPlus}
                onAction={isAdmin ? () => { setEditingItem(null); resetImageState(); setShowModal(true); } : undefined}
            >
                <NotificationTabs active="templates" />
            </PageHeader>

            {/* ── Content ── */}
            {loading ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
                    <p className="text-gray-500 text-sm">Loading templates…</p>
                </div>
            ) : error ? (
                <div className="flex flex-col items-center justify-center h-64 gap-3">
                    <p className="text-red-500 font-medium">{error}</p>
                    <button onClick={loadTemplates} className="px-4 py-2 bg-primary text-white rounded-lg text-sm">Retry</button>
                </div>
            ) : templates.length === 0 ? (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="flex flex-col items-center justify-center h-56 gap-3 text-center bg-white rounded-2xl border border-gray-100"
                >
                    <div className="w-16 h-16 rounded-2xl bg-gray-100 flex items-center justify-center">
                        <FaFileAlt className="text-gray-300 text-3xl" />
                    </div>
                    <p className="font-semibold text-gray-500 text-sm">No templates yet</p>
                    {isAdmin && (
                        <button
                            onClick={() => { setEditingItem(null); resetImageState(); setShowModal(true); }}
                            className="px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold flex items-center gap-2"
                        >
                            <FaPlus size={11} /> New Template
                        </button>
                    )}
                </motion.div>
            ) : (
                <>
                    <div className="hidden md:block bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
                        <table className="w-full text-sm">
                            <thead>
                                <tr className="border-b border-gray-100 text-left text-[11px] text-gray-500 uppercase tracking-wide">
                                    <th className="px-5 py-3 font-semibold">Code</th>
                                    <th className="px-5 py-3 font-semibold">Name</th>
                                    <th className="px-5 py-3 font-semibold">Channel</th>
                                    <th className="px-5 py-3 font-semibold">Title</th>
                                    <th className="px-5 py-3 font-semibold">Used</th>
                                    <th className="px-5 py-3 font-semibold">Updated</th>
                                    {isAdmin && <th className="px-5 py-3 font-semibold text-right">Actions</th>}
                                </tr>
                            </thead>
                            <tbody>
                                <AnimatePresence>
                                    {templates.map((t) => {
                                        const own = isOwnTemplate(t);
                                        const canEdit = isAdmin && own !== false;
                                        return (
                                            <motion.tr
                                                key={t.id}
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                exit={{ opacity: 0 }}
                                                className="border-b border-gray-50 last:border-0 odd:bg-primary/[0.03] hover:bg-primary/[0.06] transition-colors"
                                            >
                                                <td className="px-5 py-3.5 font-semibold text-gray-700">{t.code}</td>
                                                <td className="px-5 py-3.5">
                                                    <p className="font-semibold text-gray-800">{t.name}</p>
                                                    {own === false && (
                                                        <span className="text-[10px] text-gray-400">Shared template</span>
                                                    )}
                                                </td>
                                                <td className="px-5 py-3.5"><ChannelBadge channel={t.channel} /></td>
                                                <td className="px-5 py-3.5 text-gray-600 max-w-[220px] truncate">{t.title || "—"}</td>
                                                <td className="px-5 py-3.5 text-gray-700">{t.times_used ?? 0}×</td>
                                                <td className="px-5 py-3.5 text-gray-500 text-xs">{fmtDate(t.updated_at)}</td>
                                                {isAdmin && (
                                                    <td className="px-5 py-3.5">
                                                        <div className="flex items-center justify-end gap-1.5">
                                                            <button
                                                                type="button"
                                                                disabled={!canEdit}
                                                                onClick={() => { setEditingItem(t); resetImageState(); setShowModal(true); }}
                                                                title={canEdit ? "Edit" : "Shared template — not editable"}
                                                                className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-30 disabled:hover:bg-transparent"
                                                            >
                                                                <FaEdit size={11} />
                                                            </button>
                                                            <button
                                                                type="button"
                                                                disabled={!canEdit}
                                                                onClick={() => setDeletingItem(t)}
                                                                title={canEdit ? "Delete" : "Shared template — not deletable"}
                                                                className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-30 disabled:hover:bg-red-50"
                                                            >
                                                                <FaTrash size={11} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                )}
                                            </motion.tr>
                                        );
                                    })}
                                </AnimatePresence>
                            </tbody>
                        </table>
                    </div>

                    {/* Cards (mobile) */}
                    <div className="md:hidden flex flex-col gap-3">
                        <AnimatePresence>
                            {templates.map((t) => {
                                const own = isOwnTemplate(t);
                                const canEdit = isAdmin && own !== false;
                                return (
                                    <motion.div
                                        key={t.id}
                                        initial={{ opacity: 0, y: 8 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0 }}
                                        className="bg-white rounded-2xl border border-gray-100 shadow-sm p-4"
                                    >
                                        <div className="flex items-start justify-between gap-2">
                                            <div>
                                                <p className="font-semibold text-gray-800 text-sm">{t.name}</p>
                                                <p className="text-[11px] text-gray-400">{t.code}</p>
                                            </div>
                                            <ChannelBadge channel={t.channel} />
                                        </div>
                                        <p className="text-xs text-gray-500 mt-2 truncate">{t.title || "—"}</p>
                                        <div className="flex items-center justify-between mt-3">
                                            <p className="text-[11px] text-gray-400">{t.times_used ?? 0}× used · Updated {fmtDate(t.updated_at)}</p>
                                            {isAdmin && (
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        disabled={!canEdit}
                                                        onClick={() => { setEditingItem(t); resetImageState(); setShowModal(true); }}
                                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 transition-colors disabled:opacity-30"
                                                    >
                                                        <FaEdit size={11} />
                                                    </button>
                                                    <button
                                                        type="button"
                                                        disabled={!canEdit}
                                                        onClick={() => setDeletingItem(t)}
                                                        className="w-8 h-8 flex items-center justify-center rounded-lg text-red-400 bg-red-50 hover:bg-red-100 transition-colors disabled:opacity-30"
                                                    >
                                                        <FaTrash size={11} />
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </AnimatePresence>
                    </div>

                    <Pagination
                        page={page}
                        totalPages={totalPages}
                        onPrev={() => setPage((p) => Math.max(1, p - 1))}
                        onNext={() => setPage((p) => Math.min(totalPages, p + 1))}
                    />
                </>
            )}

            {/* ── Add / Edit Modal ── */}
            <AnimatePresence>
                {showModal && (
                    <Modal
                        title={editingItem ? `Edit – ${editingItem.name}` : "New Template"}
                        icon={editingItem ? FaEdit : FaPlus}
                        onClose={() => { setShowModal(false); setEditingItem(null); resetImageState(); }}
                    >
                        <Formik
                            key={editingItem?.id || "new"}
                            initialValues={getInitialValues()}
                            validationSchema={TemplateSchema}
                            onSubmit={handleSubmit}
                        >
                            {({ isSubmitting }) => (
                                <Form className="flex flex-col gap-4">
                                    <FieldWrap name="name" label="Template Name" required>
                                        <Field name="name" placeholder="e.g. Mandi Rate Update" className={inputCls} />
                                    </FieldWrap>

                                    <SearchableSelectField
                                        name="channel"
                                        label="Channel"
                                        required
                                        className={inputCls}
                                        options={CHANNEL_OPTIONS.map((c) => ({
                                            value: c.value,
                                            label: `${c.label}${!c.enabled ? " (Coming soon)" : ""}`,
                                            disabled: !c.enabled,
                                        }))}
                                    />

                                    <FieldWrap name="title" label="Title">
                                        <Field name="title" placeholder="Notification title shown in bold" className={inputCls} />
                                    </FieldWrap>

                                    <FieldWrap name="body" label="Body">
                                        <Field
                                            as="textarea"
                                            name="body"
                                            rows={4}
                                            placeholder="Write the message. Use placeholders like {farmer_name}, {commodity}, {date}, {rate}, {amount}."
                                            className={inputCls}
                                        />
                                    </FieldWrap>

                                    <div>
                                        <label className="block text-xs font-semibold text-gray-500 mb-1">Image (optional)</label>
                                        <div className="flex items-center gap-3">
                                            <div className="w-14 h-14 rounded-lg border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden flex-shrink-0">
                                                {imagePreview ? (
                                                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                                                ) : editingItem?.image ? (
                                                    <img src={resolveMediaUrl(editingItem.image)} alt="Current" className="w-full h-full object-cover" />
                                                ) : (
                                                    <FaFileAlt className="text-gray-300" />
                                                )}
                                            </div>
                                            <label className="cursor-pointer bg-primary/10 hover:bg-primary/20 text-primary font-semibold text-xs px-4 py-2.5 rounded-xl transition-colors border border-primary/20">
                                                Upload image
                                                <input type="file" accept="image/*" className="hidden" onChange={handleImageChange} />
                                            </label>
                                        </div>
                                    </div>

                                    <div className="flex gap-3 pt-2 border-t border-gray-100">
                                        <button
                                            type="button"
                                            onClick={() => { setShowModal(false); setEditingItem(null); resetImageState(); }}
                                            className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                        >
                                            Cancel
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={isSubmitting}
                                            className="flex-1 py-2.5 bg-primary hover:bg-green-600 text-white font-semibold rounded-xl text-sm shadow-lg shadow-primary/20 transition-colors disabled:opacity-60"
                                        >
                                            {isSubmitting ? "Saving…" : editingItem ? "Save Changes" : "Create Template"}
                                        </button>
                                    </div>
                                </Form>
                            )}
                        </Formik>
                    </Modal>
                )}
            </AnimatePresence>

            {/* ── Delete Confirm Modal ── */}
            <AnimatePresence>
                {deletingItem && (
                    <Modal title="Remove Template" icon={FaTrash} onClose={() => setDeletingItem(null)}>
                        <div className="flex flex-col items-center gap-4 text-center">
                            <div className="w-14 h-14 rounded-2xl bg-red-50 flex items-center justify-center">
                                <FaExclamationTriangle className="text-red-400 text-2xl" />
                            </div>
                            <div>
                                <p className="font-bold text-gray-800">Remove <span className="text-primary">"{deletingItem.name}"</span>?</p>
                                <p className="text-gray-500 text-xs mt-1">This can't be undone.</p>
                            </div>
                            <div className="flex gap-3 w-full">
                                <button
                                    onClick={() => setDeletingItem(null)}
                                    className="flex-1 py-2.5 border border-gray-200 text-gray-600 font-semibold rounded-xl text-sm hover:bg-gray-50 transition-colors"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleDelete}
                                    disabled={deleting}
                                    className="flex-1 py-2.5 bg-red-500 hover:bg-red-600 text-white font-semibold rounded-xl text-sm transition-colors disabled:opacity-60"
                                >
                                    {deleting ? "Removing…" : "Yes, Remove"}
                                </button>
                            </div>
                        </div>
                    </Modal>
                )}
            </AnimatePresence>
        </div>
    );
};

export default FPOTemplates;
